/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input, Tabs } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadTransitLCRReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";

const TransitLCRReport = () => {
    const FILTER_STORAGE_KEY = "LCR_TALLY_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(2, "days"),
        moment().subtract(1, "days")
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [statusList] = useState([
        { value: "UNLOCKED", label: "UNLOCKED" },
        { value: "LOCKED", label: "LOCKED" },

        // Add more LCR-specific statuses as needed
    ]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [stopLcrNums, setStopLcrNums] = useState("");
    const [vehicleLcrNums, setVehicleLcrNums] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState();
    const navigate = useNavigate();
    const calendarRef = useRef();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    const [activeTab, setActiveTab] = useState("current");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Transit LCR Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            selectedStatus,
            stopLcrNums,
            vehicleLcrNums,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setSelectedDestinationHub(filters.selectedDestinationHub);
                }
                if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                    setSelectedStatus(filters.selectedStatus);
                }
                if (filters.stopLcrNums) {
                    setStopLcrNums(filters.stopLcrNums);
                }
                if (filters.vehicleLcrNums) {
                    setVehicleLcrNums(filters.vehicleLcrNums);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedHubs([]);
            } else {
                setSelectedHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }
        // Only restrict for HUBADMIN with no hub selected
        if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [activeTab, hubUser, JSON.stringify(selectedHubs), JSON.stringify(selectedDestinationHub), JSON.stringify(selectedStatus), stopLcrNums, vehicleLcrNums, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            setReportData([]);
            
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedToHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;
            
            const selectedDestHubData = hubList.filter(hub => hub.value !== "ALL" && selectedDestinationHub?.includes(hub.value));

            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            const params = {
                ...(resolvedToHubs?.length > 0 && { destinationHubId: resolvedToHubs.join(",") }),
                ...(selectedDestHubData?.length > 0 && { originHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(resolvedToHubs?.length > 0 && { hubIds: resolvedToHubs }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { tripStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                ...(vehicleLcrNums && { vehicleLcrNums: vehicleLcrNums }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            console.log("Incoming LCR Report params:", params);

            // Call the report API based on active tab
            if (activeTab === "current") {
                const { data, total, count, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getLCRTallyLCRReport(params);
                setLastsynced(lastRefreshedAt);
                setNextScheduledRefresh(nextScheduledRefresh);
                setReportData(data);
                setPagination(prev => ({
                    ...prev,
                    total: typeof total === "number" ? total : (typeof count === "number" ? count : (Array.isArray(data) ? data.length : 0)),
                }));
            } else {
                const historicalParams = {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD"),
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    fromHubId: Array.isArray(selectedHubs) ? selectedHubs.filter(id => id !== "ALL").join(",") : "",
                    toHubId: Array.isArray(selectedDestinationHub) ? selectedDestinationHub.join(",") : "",
                    tripId: vehicleLcrNums,
                    stopId: stopLcrNums,
                    tripStatus: Array.isArray(selectedStatus) ? selectedStatus.join(",") : ""
                };
                const response = await ReportsService.getLCRTallyHistoricalReport(historicalParams);
                
                // Map historical data to expected camelCase keys
                const mappedData = (response.data || []).map(item => ({
                    ...item,
                    vehicleLcrNum: item.trip_id,
                    stopLcrNum: item.stop_id,
                    lcrUnlockDuration: item.unlock_duration,
                    tripStatus: item.trip_status,
                    lcrStatus: item.lcr_status,
                    tripCreatedTime: item.generated_at,
                    tripDepartedTime: item.departed_at,
                    lcrReceiveTime: item.received_at,
                    lcrCompleteTime: item.completed_at,
                    lastLcrOperationTime: item.last_activity,
                    originHub: item.from_hub,
                    destinationHub: item.to_hub,
                    vehicleNum: item.vehicle_num,
                    driverName: item.driver,
                    loadedBagCount: item.bags_loaded,
                    receivedBagCount: item.bags_received,
                    loadedShipmentCount: item.individual_shipments_loaded,
                    receivedShipmentCount: item.individual_shipments_received,
                    shipmentVariance: item.individual_shipments_variance,
                    bagVariance: item.bag_variance,
                    shipmentStatus: item.individual_shipment_status,
                }));

                setReportData(mappedData);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                }));
                setLastsynced(null);
                setNextScheduledRefresh(null);
            }
        } catch {
            message.error("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        if (activeTab === "historical") {
            message.info("Download not supported for historical data yet.");
            return;
        }
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) {
            message.error("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedToHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedDestHubData = hubList.filter(hub => hub.value !== "ALL" && selectedDestinationHub?.includes(hub.value));

            const downloadParams = {
                ...(selectedDestHubData?.length > 0 && { originHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(resolvedToHubs?.length > 0 && { destinationHubId: resolvedToHubs.join(",") }),
                ...(resolvedToHubs?.length > 0 && { hubIds: resolvedToHubs }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { tripStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                ...(vehicleLcrNums && { vehicleLcrNums: vehicleLcrNums })
            };

            await downloadTransitLCRReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        let status = type;
        // Map shipment types to required API status
        if (type === "shipmentOut") status = "SHIPMENT_OUT";
        else if (type === "shipmentIn") status = "SHIPMENT_IN";
        else if (type === "shipmentPending") status = "SHIPMENT_PENDING";
        // Map bag types to required API status
        else if (type === "bagOut") status = "BAG_OUT";
        else if (type === "bagIn") status = "BAG_IN";
        else if (type === "bagPending") status = "BAG_PENDING";
        let detailType = ["bagOut", "bagIn", "bagPending"].includes(type) ? "Bag" : "Shipment";
        let endpoint = detailType === "Bag" ? urls.BAG_DETAIL_VIEW_LCR : urls.SHIPMENT_DETAIL_VIEW_LCR;
        let lcrNum = record.stopLcrNum;
        let vehicleNum = record.vehicleNum || record.vehicle_num;
        let currentHub = record.originHub || record.fromHub || record.current_hub;
        try {
            const queryParams = {
                lcrNum,
                type: status,
            };
            const apiResult = await API.Api(endpoint).getBagDetailsParams(queryParams);
            if (apiResult.response.data.code == 200) {
                let dataArr = apiResult.response.data.data;
                if (apiResult.response.data.data.length > 0) {
                    navigate("/detailed-inout-view", {
                        state: {
                            vehicle_lcr_id: lcrNum,
                            vehicle_num: vehicleNum,
                            current_hub: currentHub,
                            type: detailType,
                            status,
                            data: dataArr,
                            pageFrom: "LCRTallyReport",
                            url: "/transit-lcr"
                        },
                    });
                } else {
                    showErrorMessage(apiResult.response.data.message);
                }
            } else {
                showErrorMessage("Something went wrong");
            }

            //console.log("apiResult:", JSON.stringify(apiResult.response.data.message, null, 2));

        } catch (error) {
            message.error("An error occurred while fetching details");
        }
    };

    return (
        <div className="report-container transit-lcr-report">
            <div style={{ backgroundColor: "white", padding: "8px 16px 0 16px", borderRadius: "8px" }}>
                <Heading
                    title="LCR Tally"
                    navigateTo="/reports"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        setPagination(prev => ({ ...prev, current: 1 }));
                        setReportData([]);
                    }}
                    className="report-tabs"
                    tabBarStyle={{ marginBottom: 0 }}
                    items={[
                        { label: "Current", key: "current" },
                        { label: "Historical", key: "historical" },
                    ]}
                />
            </div>

            <Card className="report-card" style={{ marginTop: "0" }}>
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Origin Hub"
                            options={hubList}
                            onChange={setSelectedDestinationHub}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Destination Hub"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Trip Status</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Trip Status"
                            options={statusList}
                            onChange={setSelectedStatus}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Trip ID</div>
                        <Input
                            placeholder="Enter comma separated Trip ID"
                            value={vehicleLcrNums}
                            onChange={(e) => setVehicleLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Stop ID</div>
                        <Input
                            placeholder="Enter comma separated Stop ID"
                            value={stopLcrNums}
                            onChange={(e) => setStopLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} style={{ textAlign: "right", paddingTop: "24px" }}>
                        {activeTab === "current" && (
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                loading={downloadLoading}
                                onClick={handleDownload}
                            >
                                Download Report
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row style={{ marginTop: 16 }}>

                </Row>
            </Card>
            {/* New Table for Provided Data */}
            <TableComponent
                columns={[
                    {
                        title: "Trip ID", dataIndex: "vehicleLcrNum", key: "vehicleLcrNum", sorter: true, align: "center",
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.vehicleLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Stop ID", dataIndex: "stopLcrNum", key: "stopLcrNum", sorter: true,
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.stopLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    { title: "Unlock Duration", dataIndex: "lcrUnlockDuration", key: "lcrUnlockDuration", sorter: true, align: "center" },
                    { title: "Trip Status", dataIndex: "tripStatus", key: "tripStatus", sorter: true, align: "center" },
                    { title: "Generated At", dataIndex: "tripCreatedTime", key: "tripCreatedTime", sorter: true, align: "center" },
                    { title: "Departed At", dataIndex: "tripDepartedTime", key: "tripDepartedTime", sorter: true, align: "center" },
                    { title: "Received At", dataIndex: "lcrReceiveTime", key: "lcrReceiveTime", sorter: true, align: "center" },
                    { title: "Completed At", dataIndex: "lcrCompleteTime", key: "lcrCompleteTime", sorter: true, align: "center" },
                    { title: "LCR Status", dataIndex: "lcrStatus", key: "lcrStatus", sorter: true, align: "center" },
                    { title: "Last Activity", dataIndex: "lastLcrOperationTime", key: "lastLcrOperationTime", sorter: true, align: "center" },
                    { title: "From Hub", dataIndex: "originHub", key: "originHub", sorter: true },
                    { title: "To Hub", dataIndex: "destinationHub", key: "destinationHub", sorter: true },
                    { title: "Mode", dataIndex: "mode", key: "mode", sorter: true },
                    { title: "Flight No.", dataIndex: "flightNum", key: "flightNum", sorter: true, align: "center" },
                    { title: "Co Loader", dataIndex: "coLoader", key: "coLoader", sorter: true, align: "center" },
                    { title: "Vehicle No", dataIndex: "vehicleNum", key: "vehicleNum", sorter: true, align: "center" },
                    { title: "Driver", dataIndex: "driverName", key: "driverName", sorter: true, align: "center" },
                    { title: "Driver Phone No", dataIndex: "driverPhoneNo", key: "driverPhoneNo", sorter: true, align: "center" },
                    { title: "Bag Status", dataIndex: "bagStatus", key: "bagStatus", sorter: true, align: "center" },
                    {
                        title: "Bag Loaded", dataIndex: "loadedBagCount", key: "loadedBagCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.loadedBagCount == 0 || record.loadedBagCount == null) ? <span>{record.loadedBagCount ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagOut", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    {
                        title: "Bags Received", dataIndex: "receivedBagCount", key: "receivedBagCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.receivedBagCount == 0 || record.receivedBagCount == null) ? <span>{record.receivedBagCount ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagIn", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    {
                        title: "Bag Variance", dataIndex: "bagVariance", key: "bagVariance", sorter: true, align: "center", width: 20,
                        render: (text, record) => (
                            <> {(record.bagVariance == 0 || record.bagVariance == null) ? <span>{record.bagVariance ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagPending", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    { title: "Individual Shipments Status", dataIndex: "shipmentStatus", key: "shipmentStatus", sorter: true, align: "center" },
                    {
                        title: "Individual Shipments Loaded", dataIndex: "loadedShipmentCount", key: "loadedShipmentCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.loadedShipmentCount == 0 || record.loadedShipmentCount == null) ? <span>{record.loadedShipmentCount ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentOut", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Received", dataIndex: "receivedShipmentCount", key: "receivedShipmentCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.receivedShipmentCount == 0 || record.receivedShipmentCount == null) ? <span>{record.receivedShipmentCount ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentIn", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Variance", dataIndex: "shipmentVariance", key: "shipmentVariance", sorter: true, align: "center", width: 20,
                        render: (text, record) => (
                            <> {(record.shipmentVariance == 0 || record.shipmentVariance == null) ? <span>{record.shipmentVariance ?? "---"}</span> : (
                                activeTab === "historical" ? <span>{text}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentPending", record)}
                                    >
                                        {text}
                                    </span>
                                ))}
                            </>
                        )
                    },
                    { title: "Total Weight (kg)", dataIndex: "totalWeight", key: "totalWeight", sorter: true, align: "center" },
                    { title: "Delay", dataIndex: "delay", key: "delay", sorter: true, align: "center" },
                    { title: "Is Anomaly", dataIndex: "isAnomaly", key: "isAnomaly", sorter: true, align: "center", },
                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="stopLcrNum"
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default TransitLCRReport;
